import React, {
  useRef, useState, useLayoutEffect,
} from "react"
import { withMicrocopy } from "~contexts/MicrocopyContext"
import Pill from "~components/Pill"
import Collapsible from '~components/Collapsible'
import SidebarResource from "./SidebarResource"
import SidebarEndpoint from "./SidebarEndpoint"
import { isResourcePath } from "./utils"
import { compareVersions } from '../../../utils/versioning'
import { prioritize, sortBy } from '../../../utils/sort'
import * as style from "./Sidebar.module.scss"

const SidebarTag = ({
  tag,
  versions = [],
  endpointsByVersion,
  resourcesByVersion,
  highlightSelected = true,
  withVersion,
  itemsWithVersion,
  open,
  bodyHeight,
  deprecated,
  identLevel = 0,
  microcopy,
  location,
}) => {
  const isResource = isResourcePath(location.pathname)
  const toLookFor = isResource ? resourcesByVersion : endpointsByVersion
  const pathToLookFor = location.pathname
  const isSelected = (deprecated ? !isResource : true)
    && toLookFor && (Object.values(toLookFor) || []).flat().some((e) => e.path === pathToLookFor)

  const numberOfEndpointsVersions = Object.keys(endpointsByVersion || {}).length
  const numberOfResourcesVersions = Object.keys(resourcesByVersion || {}).length

  const shouldShowVersion = itemsWithVersion || numberOfEndpointsVersions > 1 || numberOfResourcesVersions > 1
    || (numberOfEndpointsVersions === 1 && numberOfResourcesVersions === 1
          && Object.keys(endpointsByVersion || {})?.[0] !== Object.keys(resourcesByVersion || {})?.[0])

  const renderGroupTitle = (group, version, showVersion) => (showVersion ? `${group} v${version}` : group)

  const tagRef = useRef(null)
  const endpointsRef = useRef(null)
  const resourcesRef = useRef(null)
  const selectedItemRef = useRef(null)

  const [tagHeight, setTagHeight] = useState(0)

  useLayoutEffect(() => {
    if (tagRef.current) {
      const { height } = tagRef.current.getBoundingClientRect()
      setTagHeight(height)
    }
  }, [])

  useLayoutEffect(() => {
    if (isSelected) {
      const buffer = 20
      const scrollConfig = { behavior: "instant", block: "center" }
      if (tagHeight + buffer <= bodyHeight) {
        tagRef.current?.scrollIntoView(scrollConfig)
      // eslint-disable-next-line max-len
      } else if (isResource && resourcesRef.current && resourcesRef.current.getBoundingClientRect().height + buffer <= bodyHeight) {
        resourcesRef.current.scrollIntoView(scrollConfig)
      } else if (endpointsRef.current && endpointsRef.current.getBoundingClientRect().height + buffer <= bodyHeight) {
        endpointsRef.current.scrollIntoView(scrollConfig)
      } else {
        selectedItemRef.current?.scrollIntoView(scrollConfig)
      }
    }
  }, [bodyHeight, tagHeight])

  return (
    <Collapsible
      header={(
        <h3 className={style.colHeader} data-pill={tag.isBeta}>
          <span className={highlightSelected && isSelected ? style.activeParent : ''}>
            {withVersion || tag.withVersion ? `${tag.name} v${tag.version}` : tag.name}
          </span>
          {tag.isBeta && (
            <Pill
              pillType="pillSmall"
              text={microcopy.text("reference.pill.beta")}
              color="blue"
              className={style.tagPill}
            />
          )}
        </h3>
          )}
      open={open || isSelected}
      headerClassName={`${style[`level_${identLevel + 1}`]} ${style.collapsibleHeader}`}
      className={`${style.collapsible} ${style.tag}`}
      id={tag.id}
      elementRef={tagRef}
    >
      <ul className={style.collapsibleBody}>
        {notEmpty(resourcesByVersion) && Object.entries(resourcesByVersion)
          .sort((a, b) => compareVersions(a[0], b[0]))
          .map(([version, resources]) => (
            <li key={`resources-${version}`} tabIndex={0} ref={resourcesRef}>
              <Collapsible
                header={(
                  <h4
                    className={highlightSelected && isSelected && isResource ? style.activeParent : ''}
                  >
                    <span>
                      {renderGroupTitle(microcopy.text("search.filter_types.resources"), version, shouldShowVersion)}
                    </span>
                  </h4>
                  )}
                open={open || isSelected}
                className={`${style.collapsible}`}
                headerClassName={`${style[`level_${identLevel + 2}`]} ${style.collapsibleHeader}`}
              >
                <ul>
                  {resources?.sort(sortBy('key'))?.map((resource) => (
                    <li key={resource.id} ref={resource.path === pathToLookFor ? selectedItemRef : undefined}>
                      <SidebarResource
                        resource={resource}
                        location={location}
                          // eslint-disable-next-line max-len
                        className={`${style[`level_${identLevel + 3}`]} ${highlightSelected && isResource && resource.path === pathToLookFor ? style.activeEntry : ''}`}
                      />
                    </li>
                  ))}
                </ul>
              </Collapsible>
            </li>
          ))}
        {notEmpty(endpointsByVersion) && Object.entries(endpointsByVersion)
          .sort((a, b) => compareVersions(a[0], b[0]))
          .map(([version, endpoints]) => (
            <li key={`endpoints-${version}`} tabIndex={0} ref={endpointsRef}>
              <Collapsible
                header={(
                  <h4
                    data-pill={versions?.[version]?.deprecated}
                    className={highlightSelected && isSelected && !isResource ? style.activeParent : ''}
                  >
                    <span>
                      {renderGroupTitle(microcopy.text("search.filter_types.endpoints"), version, shouldShowVersion)}
                    </span>
                    {versions?.[version]?.deprecated && (
                      <Pill
                        pillType="pillSmall"
                        text={microcopy.text("reference.pill.deprecated")}
                        color="orange"
                        className={style.tagPill}
                      />
                    )}
                  </h4>
                  )}
                open={open || isSelected}
                className={`${style.collapsible}`}
                headerClassName={`${style[`level_${identLevel + 2}`]} ${style.collapsibleHeader}`}
              >
                <ul>
                  {endpoints.sort(sortBy('key')).sort(prioritize).map((endpoint) => (
                    <li key={endpoint.id} ref={endpoint.path === pathToLookFor ? selectedItemRef : undefined}>
                      <SidebarEndpoint
                        endpoint={endpoint}
                        location={location}
                          // eslint-disable-next-line max-len
                        className={`${style[`level_${identLevel + 3}`]} ${highlightSelected && !isResource && endpoint.path === pathToLookFor ? style.activeEntry : ''}`}
                        microcopy={microcopy}
                      />
                    </li>
                  ))}
                </ul>
              </Collapsible>
            </li>
          ))}
      </ul>
    </Collapsible>
  )
}

const notEmpty = (obj) => obj && Object.keys(obj).length > 0 && obj[Object.keys(obj)[0]].length > 0

export default withMicrocopy(SidebarTag)
